import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { buildPatientProfilePreviousPage } from '@utils/patientProfileNavigation.util'
import { formatCurrency } from '@utils/billing.utils'
import { formatNumber } from '@utils/numberFormatter.util'
import {
  DEMO_ACCOUNT_AR,
  DEMO_ACCOUNT_SUMMARY,
  type AccountSummaryPaymentMethodItem,
  type AccountSummarySavedCardItem,
  type PatientAccountSummaryData,
} from '@assets/mockdata/PatientTray/mockAccountSummary'
import {
  ARAgingSummaryReport,
  PatientARAnalytics,
  SidePanelAnalytics,
} from '@services/api/billing/analytics.api'
import {
  calculateAgingPeriods,
  resolveArAgingTotal,
  type BillingAgingPeriod,
} from '@utils/billingArAnalytics.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  fetchARAging,
  fetchInsuranceARAnalytics,
  fetchPatientARAnalytics,
  fetchSidePanelAnalytics,
} from '@store/actions/billing.action'
import usePatientProfilePaymentMethodActions from '@pages/PatientProfile/shared/usePatientProfilePaymentMethodActions'

export type UsePatientProfileAccountSummary = {
  data: PatientAccountSummaryData
  paymentMethods: AccountSummaryPaymentMethodItem[]
  savedCards: AccountSummarySavedCardItem[]
  patientARAnalytics: PatientARAnalytics | null
  insuranceARAnalytics: PatientARAnalytics | null
  arAgingReport: ARAgingSummaryReport | null
  agingPeriods: BillingAgingPeriod[]
  arAgingTotal: number
  isLoading: boolean
  balanceLeftRows: Array<{ label: string; value: string; medium?: boolean }>
  balanceRightRows: Array<{ label: string; value: string }>
  showDeleteModal: boolean
  deleteModalTitle: string
  deleteModalBody?: string
  handleRequestDeletePaymentMethod: (
    item: AccountSummaryPaymentMethodItem,
  ) => void
  handleRequestDeleteSavedCard: (item: AccountSummarySavedCardItem) => void
  handleCloseDeleteModal: () => void
  handleConfirmDelete: () => Promise<void>
  isDeleting: boolean
  isSettingDefault: boolean
  isPaymentNetworkBusy: boolean
  handleSetDefaultPaymentMethod: (item: AccountSummaryPaymentMethodItem) => void
  handleSetDefaultSavedCard: (item: AccountSummarySavedCardItem) => void
  navigateToLedger: () => void
}

type UsePatientProfileAccountSummaryOptions = {
  useDemoData?: boolean
}

const usePatientProfileAccountSummary = (
  options: UsePatientProfileAccountSummaryOptions = {},
): UsePatientProfileAccountSummary => {
  const { useDemoData = false } = options
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const patientARAnalytics = useAppSelector(
    (state) => state.billing.patientARAnalytics,
  )
  const insuranceARAnalytics = useAppSelector(
    (state) => state.billing.insuranceARAnalytics,
  )
  const arAgingReport = useAppSelector((state) => state.billing.arAgingReport)
  const sidePanelAnalytics = useAppSelector(
    (state) => state.billing.sidePanelAnalytics,
  )
  const loadingPatientARAnalytics = useAppSelector(
    (state) => state.billing.loadingPatientARAnalytics,
  )
  const loadingInsuranceARAnalytics = useAppSelector(
    (state) => state.billing.loadingInsuranceARAnalytics,
  )
  const loadingARAging = useAppSelector((state) => state.billing.loadingARAging)
  const loadingSidePanelAnalytics = useAppSelector(
    (state) => state.billing.loadingSidePanelAnalytics,
  )

  const patientId = selectedPatient?.patient_id
  const clinicId = selectedClinic?.clinic_id

  const {
    paymentMethods: livePaymentMethods,
    savedCards: liveSavedCards,
    isLoading: loadingPaymentMethods,
    isDeleting,
    isSettingDefault,
    isPaymentNetworkBusy,
    showDeleteModal,
    deleteModalTitle,
    deleteModalBody,
    handleRequestDeletePaymentMethod,
    handleRequestDeleteSavedCard,
    handleCloseDeleteModal,
    handleConfirmDelete,
    handleSetDefaultPaymentMethod,
    handleSetDefaultSavedCard,
  } = usePatientProfilePaymentMethodActions({ enabled: !useDemoData })

  useEffect(() => {
    if (useDemoData || !patientId || !clinicId) return

    dispatch(fetchPatientARAnalytics())
    dispatch(fetchInsuranceARAnalytics())
    dispatch(fetchARAging())
    dispatch(fetchSidePanelAnalytics())
  }, [dispatch, patientId, clinicId, useDemoData])

  const agingPeriods = useMemo(
    () =>
      useDemoData
        ? DEMO_ACCOUNT_AR.agingPeriods
        : calculateAgingPeriods(arAgingReport, patientId, clinicId),
    [arAgingReport, patientId, clinicId, useDemoData],
  )

  const arAgingTotal = useMemo(
    () =>
      useDemoData
        ? DEMO_ACCOUNT_AR.arAgingTotal
        : resolveArAgingTotal(arAgingReport, patientId, clinicId),
    [arAgingReport, patientId, clinicId, useDemoData],
  )

  const paymentMethods = useDemoData
    ? DEMO_ACCOUNT_SUMMARY.paymentMethods
    : livePaymentMethods
  const savedCards = useDemoData
    ? DEMO_ACCOUNT_SUMMARY.savedCards
    : liveSavedCards

  const data: PatientAccountSummaryData = useMemo(() => {
    if (useDemoData) {
      return DEMO_ACCOUNT_SUMMARY
    }

    const overview: SidePanelAnalytics = sidePanelAnalytics ?? {
      insuranceBalance: 0,
      patientBalance: 0,
      familyBalance: 0,
      discountApplied: 0,
      insuranceTotal: 0,
      patientTotal: 0,
      outstandingTotal: 0,
      walletBalance: 0,
    }

    return {
      balances: {
        outstanding: overview.patientBalance + overview.insuranceBalance,
        insurance: overview.insuranceBalance,
        patient: overview.patientBalance,
        family: overview.familyBalance,
        discountApplied: overview.discountApplied,
        renewalMonth: 'None',
      },
      paymentMethods,
      savedCards,
    }
  }, [paymentMethods, savedCards, sidePanelAnalytics, useDemoData])

  const balanceLeftRows = [
    {
      label: 'Insurance balance:',
      value: formatCurrency(data.balances.insurance),
    },
    {
      label: 'Patient balance:',
      value: formatCurrency(data.balances.patient),
    },
    {
      label: 'Renewal month:',
      value: data.balances.renewalMonth,
      medium: true,
    },
  ]

  const balanceRightRows = [
    {
      label: 'Family balance:',
      value: formatNumber(data.balances.family, true),
    },
    {
      label: 'Discount applied:',
      value: formatNumber(data.balances.discountApplied, true),
    },
  ]

  const isLoading =
    !useDemoData &&
    (loadingPatientARAnalytics ||
      loadingInsuranceARAnalytics ||
      loadingARAging ||
      loadingSidePanelAnalytics ||
      loadingPaymentMethods)

  const navigateToLedger = useCallback((): void => {
    navigate('/billing/ledger', {
      ...(patientId
        ? {
            state: {
              previousPage: buildPatientProfilePreviousPage(
                patientId,
                'account',
              ),
            },
          }
        : {}),
    })
  }, [navigate, patientId])

  return {
    data,
    paymentMethods,
    savedCards,
    patientARAnalytics: useDemoData
      ? DEMO_ACCOUNT_AR.patient
      : patientARAnalytics,
    insuranceARAnalytics: useDemoData
      ? DEMO_ACCOUNT_AR.insurance
      : insuranceARAnalytics,
    arAgingReport: useDemoData ? null : arAgingReport,
    agingPeriods,
    arAgingTotal,
    isLoading,
    balanceLeftRows,
    balanceRightRows,
    showDeleteModal: useDemoData ? false : showDeleteModal,
    deleteModalTitle,
    deleteModalBody,
    handleRequestDeletePaymentMethod,
    handleRequestDeleteSavedCard,
    handleCloseDeleteModal,
    handleConfirmDelete,
    isDeleting: useDemoData ? false : isDeleting,
    isSettingDefault: useDemoData ? false : isSettingDefault,
    isPaymentNetworkBusy: useDemoData ? false : isPaymentNetworkBusy,
    handleSetDefaultPaymentMethod,
    handleSetDefaultSavedCard,
    navigateToLedger,
  }
}

export default usePatientProfileAccountSummary
